import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { CompetitionsGrid } from './components/CompetitionsGrid';
import { CompetitionsFilterTabs } from './components/CompetitionsFilterTabs';
import { CompetitionsOverviewFilter } from './components/CompetitionsFilterTabs/interfaces';
import { ContentWithSidebar } from '../../../../components/ContentWithSidebar';
import { PageTitle } from '../../../../components/PageTitle';
import { Advertisement, RankList } from '@components/content-side-bar';
import { TRank } from '@components/content-side-bar/RankList/interfaces';
import { rankingData } from '../CompetitionDetails/helpers';
import { useGetPortalCompetitions } from '@reactQuery/competitions';
import { mapPortalCompetitionToCard } from '../helpers';
import { Loader } from '@components/feed/Loader';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getPortalCompetitions } from '@api/competitionsApi';

export const Overview: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const [activeFilter, setActiveFilter] =
    useState<CompetitionsOverviewFilter>('all');

  const { data: enrolledCountData } = useQuery({
    queryKey: ['portal-competitions-enrolled-count'],
    queryFn: () => getPortalCompetitions(1, 1, { enrolledOnly: true }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const enrolledCount = enrolledCountData?.meta.total ?? 0;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetPortalCompetitions({
      perPage: 12,
      enrolledOnly: activeFilter === 'mine',
    });

  const competitions = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.data.map(mapPortalCompetitionToCard)
      ) ?? [],
    [data]
  );

  useEffect(() => {
    if (!loaderRef.current || !hasNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);

  const rankingSP: TRank[] = [
    {
      position: 1,
      name: 'Jane Doe',
      avatarSrc: '/dummy/avatars/avatar3.jpg',
      score: 300,
    },
    {
      position: 2,
      name: 'Jaap Foo',
      avatarSrc: '/dummy/avatars/avatar1.jpg',
      score: 288,
    },
    {
      position: 3,
      name: 'John Doe',
      avatarSrc: '/dummy/avatars/avatar4.jpg',
      score: 278,
    },
  ];

  const rankingCarbine: TRank[] = [
    {
      position: 1,
      name: 'Andries Faber',
      avatarSrc: '/dummy/avatars/avatar3.jpg',
      score: 292.4,
    },
    {
      position: 2,
      name: 'Peter Miltenburg',
      avatarSrc: '/dummy/avatars/avatar1.jpg',
      score: 260.4,
    },
    {
      position: 3,
      name: 'Ron Veenhof',
      avatarSrc: '/dummy/avatars/avatar4.jpg',
      score: 274.3,
    },
  ];

  const rankingPlate: TRank[] = [
    {
      position: 1,
      name: 'Sanne Smits',
      avatarSrc: '/dummy/avatars/avatar3.jpg',
      score: 200.4,
    },
    {
      position: 2,
      name: 'Milan Vos',
      avatarSrc: '/dummy/avatars/avatar1.jpg',
      score: 190.1,
    },
    {
      position: 3,
      name: 'Nathan van Dam',
      avatarSrc: '/dummy/avatars/avatar4.jpg',
      score: 180.3,
    },
  ];

  const Sidebar = (
    <Fragment>
      <RankList
        ranking={rankingData.slice(0, 3).map((item, index) => {
          return {
            position: index + 1,
            name: item.name,
            avatarSrc: item.avatarSource,
            score: item.totalScore,
          };
        })}
        title="KKG 100m Optiek"
      />
      <RankList ranking={rankingCarbine} title="Karabijn 50m 2026" />
      <RankList ranking={rankingSP} title="Service Pistool 2026" />
      <RankList ranking={rankingPlate} title="Plate Shooting 2026" />
      <Advertisement />
    </Fragment>
  );

  return (
    <ContentWithSidebar sidebarContent={Sidebar}>
      <PageTitle
        title="Lopende competities"
        subTitle="Blijf op de hoogte van de spannendste wedstrijden en actuele scores!"
      />

      <CompetitionsFilterTabs
        value={activeFilter}
        enrolledCount={enrolledCount}
        onChange={setActiveFilter}
      />

      {!isLoading && competitions.length > 0 && (
        <CompetitionsGrid competitions={competitions} />
      )}

      {!isLoading && competitions.length === 0 && (
        <EmptyPlaceholder
          icon={EmojiEventsIcon}
          title={t(
            activeFilter === 'mine'
              ? 'competitions:portal.overview.emptyPlaceholders.noEnrolledCompetitions.title'
              : 'competitions:portal.overview.emptyPlaceholders.noCompetitions.title'
          )}
          subTitle={t(
            activeFilter === 'mine'
              ? 'competitions:portal.overview.emptyPlaceholders.noEnrolledCompetitions.subTitle'
              : 'competitions:portal.overview.emptyPlaceholders.noCompetitions.subTitle'
          )}
        />
      )}

      {(hasNextPage || isLoading) && (
        <Box ref={loaderRef} py={2} minHeight={40}>
          {(isLoading || isFetchingNextPage) && <Loader />}
        </Box>
      )}
    </ContentWithSidebar>
  );
};
